// "What changed" of a conflict, and the rebase of my changes on their
// version (phase 3, design C and D.3). Pure: no MemoryDB, no React.
//
// Three versions of a row:
// - base: the stored row my changes are based on (the journal's original,
//   or the base an open form is held on);
// - theirs: the stored row now (the colleague's save; null: deleted);
// - mine: my row now (the journal's current, or the form's values; null:
//   I deleted it).
//
// describe({ base, theirs, mine, accessor, limit, titleOf })
//   => { lines: [{ path, label, side, kind, base, theirs, mine }], more }
// merge({ base, theirs, mine, accessor })
//   => { ok, row, both: [path], reason }
//
// How they compare:
// - every row goes through deriveRow (hooks/itemRow.js) first: what the form
//   adds to a stored row (an _id on each sub-block of a block, the
//   prevReference of an endpoint) is no change, whichever version has it;
// - booleans are 1/0, at any depth (a full load stores the top-level flags
//   as 1/0, a row of the socket does not); undefined is no field;
// - not compared: refsComputed, lastSaved, version, refs (refs.belongs is
//   the server's; refs.require is derived from the content: see merge);
//   theirs always wins on these;
// - conditions: `condition` (the string) is compared, never `conditionRaw`,
//   which goes with its condition when merged;
// - keyed arrays, compared element by element: the sub-blocks of a block
//   (by _id, else id), the variants of an endpoint (by blockId), the
//   statements of a rule (by _id). Any other array, and any `layout`, is
//   one value. The _id deriveRow gives a sub-block stored without one is
//   its position, no identity: a side whose sub-blocks are stored without
//   ids that changed them, or theirs with other sub-blocks (or another
//   order) than a base stored without ids, make the array one value;
//   unless theirs is the save right after base and its stored ids are all
//   base's derived ones (their form saved the ids it derived from base:
//   they name base's sub-blocks);
// - the tree of a property is one value, described as the pages added,
//   removed, renamed or moved.
//
// The three-way rule: a field is "both" when both sides changed it to
// different values, or when one side changed it and the other an ancestor
// or a descendant of it (an edit of a sub-block its colleague deleted). A
// keyed array is merged element by element only when at most one side
// changed which elements it has or their order (or both changed them the
// same way); otherwise it is one value.

import cloneDeep from "lodash.clonedeep";
import isEqual from "lodash.isequal";
import { deriveRow } from "../itemRow";
import { labelOf, pathOf } from "./diffLabels";

// Bounds of describe (merge always goes as deep as the depth bound and
// looks at every element)
export const MAX_DEPTH = 4;
export const MAX_VALUE = 80;
export const MAX_ELEMENTS = 20;
export const LIMIT = 30;

// Lines sorted by side: the clashes first
const RANK = { both: 0, theirs: 1, mine: 2 };

const NOT_COMPARED = new Set(["refsComputed", "lastSaved", "version", "refs"]);

const KEYS = {
  blocks: { blocks: (element) => element?._id ?? element?.id },
  endpoints: { variants: (element) => element?.blockId },
  rules: { rule: (element) => element?._id }
};

const isPlain = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// The value as it is compared: booleans as 1/0, no undefined field, no
// conditionRaw
function norm(value) {
  if (value === true) return 1;
  if (value === false) return 0;
  if (Array.isArray(value))
    return value.map((item) => (item === undefined ? null : norm(item)));
  if (isPlain(value)) {
    const out = {};
    for (const [key, item] of Object.entries(value)) {
      if (item === undefined || key === "conditionRaw") continue;
      out[key] = norm(item);
    }
    return out;
  }
  return value;
}

// A row as it is compared (the fields not compared left out)
function normRow(row) {
  const out = {};
  for (const [key, value] of Object.entries(row)) {
    if (NOT_COMPARED.has(key) || value === undefined) continue;
    if (key === "conditionRaw") continue;
    out[key] = norm(value);
  }
  return out;
}

// The keys of a keyed array, in order; null when an element has none or two
// have the same (the array is then one value)
function keysOf(array, keyOf) {
  if (!Array.isArray(array)) return null;
  const keys = [];
  const seen = new Set();
  for (const element of array) {
    const key = keyOf(element);
    if (key === undefined || key === null || key === "" || seen.has(key))
      return null;
    seen.add(key);
    keys.push(String(key));
  }
  return keys;
}

const indexOf = (keys) => new Map(keys.map((key, index) => [key, index]));

// The keys of a keyed array are positions, not identities, when deriveRow
// made them: the sub-blocks of a block stored without an _id get
// `${row.id}_${index}`, so the same key names another sub-block once a
// colleague removed, added or moved sub-blocks without saving ids (e.g. the
// DB explorer). keys: those of the derived array; stored: the array as
// stored (before deriveRow).
const positional = (stored, keys, keyOf) => {
  const own = keysOf(stored, keyOf);
  return !own || !isEqual(own, keys);
};

// Theirs' stored keys name base's elements although base's keys are
// positions: the colleague's form derived them from base (`${row.id}_${index}`
// of base's sub-blocks) and saved them, removing or moving sub-blocks. Only
// when theirs is the save right after base (base's version + 1: their form
// was opened on base itself, not on a version between, e.g. one the DB
// explorer saved with sub-blocks removed, whose positions are not base's)
// and every key of theirs is one of base's. kt: theirs' keys, stored (not
// positional); kb: base's, derived.
const namesBase = (kt, kb, [base, theirs]) =>
  Number(theirs?.version) === Number(base?.version ?? 0) + 1 &&
  kt.every((key) => kb.includes(key));

// --- the walk: the changes of a path, and its merged value -----------------
//
// n: [base, theirs, mine] as compared; r: the same, raw. Returns { value
// (raw, undefined: no field), from ("theirs" | "mine" | "merged"), changes,
// both }. A change: { segs, side, kind, r, same? }.

const change = (segs, side, kind, r, extra) => ({
  segs,
  side,
  kind,
  r,
  ...extra
});

function leaf(segs, [b, t, m], r, kind = "value") {
  const [, rt, rm] = r;
  const theirsChanged = !isEqual(b, t);
  const mineChanged = !isEqual(b, m);

  if (!mineChanged)
    return {
      value: rt,
      from: "theirs",
      changes: [change(segs, "theirs", kind, r)],
      both: []
    };
  if (!theirsChanged)
    return {
      value: rm,
      from: "mine",
      changes: [change(segs, "mine", kind, r)],
      both: []
    };
  if (isEqual(t, m))
    return {
      value: rt,
      from: "theirs",
      changes: [change(segs, "theirs", kind, r, { same: true })],
      both: []
    };
  return {
    value: rt,
    from: "theirs",
    changes: [change(segs, "both", kind, r)],
    both: [pathOf(segs)]
  };
}

// More than MAX_ELEMENTS children changed: one line per side, a count of
// the children (fields or elements) each side changed. children: [{ kind,
// changes }], one per child changed.
function counted(segs, changes, children) {
  if (children.length <= MAX_ELEMENTS) return changes;

  const sides = new Map();
  for (const child of children) {
    for (const side of new Set(child.changes.map(({ side }) => side))) {
      const count = sides.get(side) ?? { changed: 0, added: 0, removed: 0 };
      if (child.kind === "added") count.added++;
      else if (child.kind === "removed") count.removed++;
      else count.changed++;
      sides.set(side, count);
    }
  }
  return [...sides]
    .sort(([x], [y]) => RANK[x] - RANK[y])
    .map(([side, count]) =>
      change(segs, side, "count", [undefined, undefined, undefined], {
        count
      })
    );
}

function objectNode(ctx, segs, [b, t, m], [rb, rt, rm]) {
  const keys = [
    ...new Set([...Object.keys(t), ...Object.keys(m), ...Object.keys(b)])
  ];
  const value = { ...rt };
  let changes = [];
  const both = [];
  const children = [];
  let conditionFrom = null;

  for (const key of keys) {
    const sub = walk(
      ctx,
      [...segs, key],
      [b[key], t[key], m[key]],
      [rb?.[key], rt?.[key], rm?.[key]]
    );
    if (sub.value === undefined) delete value[key];
    else value[key] = sub.value;
    if (key === "condition") conditionFrom = sub.from;
    if (sub.changes.length)
      children.push({ kind: "changed", changes: sub.changes });
    changes = changes.concat(sub.changes);
    both.push(...sub.both);
  }

  // conditionRaw goes with its condition, from the same side
  if (conditionFrom === "mine") {
    if (rm?.conditionRaw === undefined) delete value.conditionRaw;
    else value.conditionRaw = rm.conditionRaw;
  }

  return {
    value,
    from: "merged",
    changes: counted(segs, changes, children),
    both
  };
}

function keyedNode(ctx, segs, n, r, keyOf) {
  const [b, t, m] = n;
  const [rb, rt, rm] = r;
  const kb = keysOf(rb, keyOf);
  const kt = keysOf(rt, keyOf);
  const km = keysOf(rm, keyOf);
  if (!kb || !kt || !km) return leaf(segs, n, r, "array");

  // Positional keys pair the elements only where nobody moved them. Mine
  // derive from base (the form, the draft), so my keys name base's elements;
  // theirs come from any writer. One value when a side whose keys are
  // positions changed the array (a removal, a move or an edit can't be
  // told apart), or when base's keys are positions and theirs has other
  // elements, or another order, unless theirs' stored keys name base's
  // elements (namesBase: their form saved the ids it derived from base).
  const [sb, st, sm] = ctx.stored.map((row) => row?.[segs[0]]);
  if (
    (!isEqual(b, t) &&
      (positional(st, kt, keyOf) ||
        (positional(sb, kb, keyOf) &&
          !isEqual(kb, kt) &&
          !namesBase(kt, kb, ctx.stored)))) ||
    (!isEqual(b, m) && positional(sm, km, keyOf))
  )
    return leaf(segs, n, r, "array");

  const theirsMoved = !isEqual(kb, kt);
  const mineMoved = !isEqual(kb, km);
  // Both changed which elements there are, or their order, each in its own
  // way: the array is one value
  if (theirsMoved && mineMoved && !isEqual(kt, km))
    return leaf(segs, n, r, "array");

  const order = mineMoved && !theirsMoved ? km : kt;
  const [ib, it, im] = [indexOf(kb), indexOf(kt), indexOf(km)];
  const all = [...new Set([...order, ...kb])];
  const values = new Map();
  let changes = [];
  const both = [];
  const children = [];

  for (const key of all) {
    const [i, j, k] = [ib.get(key), it.get(key), im.get(key)];
    const element = rm?.[k] ?? rt?.[j] ?? rb?.[i];
    const index = k ?? j ?? i;
    const at = [...segs, { key, element, index }];
    const raw = [rb[i], rt[j], rm[k]];
    const inB = i !== undefined;
    const inT = j !== undefined;
    const inM = k !== undefined;
    let sub;

    if (inB && inT && inM) sub = walk(ctx, at, [b[i], t[j], m[k]], raw);
    else if (!inB && inT && inM)
      // Added by both (the same structural change): one value
      sub = leaf(at, [undefined, t[j], m[k]], raw, "added");
    else if (!inB)
      sub = {
        value: inT ? rt[j] : rm[k],
        changes: [change(at, inT ? "theirs" : "mine", "added", raw)],
        both: []
      };
    else if (!inT && !inM)
      sub = {
        value: undefined,
        changes: [change(at, "theirs", "removed", raw, { same: true })],
        both: []
      };
    else {
      // Removed by one side: a conflict when the other changed it
      const kept = inT ? t[j] : m[k];
      const conflict = !isEqual(b[i], kept);
      sub = {
        value: undefined,
        changes: [
          change(
            at,
            conflict ? "both" : inT ? "mine" : "theirs",
            "removed",
            raw
          )
        ],
        both: conflict ? [pathOf(at)] : []
      };
      // Their element stays (the merge is refused anyway)
      if (conflict && inT) sub.value = rt[j];
    }

    values.set(key, sub.value);
    if (sub.changes.length)
      children.push({
        kind: !inB ? "added" : inT && inM ? "changed" : "removed",
        changes: sub.changes
      });
    changes = changes.concat(sub.changes);
    both.push(...sub.both);
  }

  return {
    value: order
      .map((key) => values.get(key))
      .filter((value) => value !== undefined),
    from: "merged",
    changes: counted(segs, changes, children),
    both
  };
}

// The pages of a tree: value -> { label, parent, index }
function flatTree(tree, parent = null, out = new Map()) {
  (Array.isArray(tree) ? tree : []).forEach((node, index) => {
    if (!node || node.value === undefined) return;
    out.set(String(node.value), { label: node.label, parent, index });
    if (Array.isArray(node.children))
      flatTree(node.children, String(node.value), out);
  });
  return out;
}

const quoted = (labels) => labels.map((label) => `«${label}»`).join(", ");
const listed = (word, labels) =>
  labels.length > 3
    ? `${labels.length} ${word}`
    : labels.length
      ? `${word} ${quoted(labels)}`
      : null;

// "added «A»; removed «B»; renamed «C» → «D»; moved «E»": what a side did to
// the pages of the tree of base
export function treeSummary(base, side) {
  if (side === undefined || side === null) return "no tree";
  const before = flatTree(base);
  const after = flatTree(side);
  const added = [];
  const removed = [];
  const renamed = [];
  const moved = [];

  for (const [value, node] of after) {
    const old = before.get(value);
    if (!old) {
      added.push(node.label ?? value);
      continue;
    }
    if (old.label !== node.label)
      renamed.push(`«${old.label ?? value}» → «${node.label ?? value}»`);
    if (old.parent !== node.parent) moved.push(node.label ?? value);
  }
  for (const [value, node] of before)
    if (!after.has(value)) removed.push(node.label ?? value);

  // The same pages in a folder, in another order
  const reordered = [];
  const childrenOf = (map, folder) =>
    [...map]
      .filter(([, node]) => node.parent === folder)
      .sort((x, y) => x[1].index - y[1].index)
      .map(([value]) => value);
  const folders = new Set([...after.values()].map(({ parent }) => parent));
  for (const folder of folders) {
    const now = childrenOf(after, folder).filter(
      (value) => before.get(value)?.parent === folder
    );
    const then = childrenOf(before, folder).filter((value) =>
      now.includes(value)
    );
    if (!isEqual(now, then))
      reordered.push(
        folder === null ? "top" : (after.get(folder)?.label ?? folder)
      );
  }

  const parts = [
    listed("added", added),
    listed("removed", removed),
    renamed.length > 3
      ? `${renamed.length} renamed`
      : renamed.length
        ? `renamed ${renamed.join(", ")}`
        : null,
    listed("moved", moved),
    listed("reordered in", reordered)
  ].filter(Boolean);
  return parts.length ? parts.join("; ") : "changed";
}

function walk(ctx, segs, n, r) {
  const [b, t, m] = n;
  if (isEqual(b, t) && isEqual(b, m))
    return { value: r[1], from: "theirs", changes: [], both: [] };

  const field = segs[segs.length - 1];
  if (ctx.accessor === "properties" && segs.length === 1 && field === "tree")
    return leaf(segs, n, r, "tree");

  const keyOf = segs.length === 1 && KEYS[ctx.accessor]?.[field];
  if (keyOf && Array.isArray(b) && Array.isArray(t) && Array.isArray(m))
    return keyedNode(ctx, segs, n, r, keyOf);

  if (
    field !== "layout" &&
    segs.length < MAX_DEPTH &&
    isPlain(b) &&
    isPlain(t) &&
    isPlain(m)
  )
    return objectNode(ctx, segs, n, r);

  return leaf(segs, n, r);
}

// The three rows as compared and raw (derived as the form derives them),
// and as stored (the keys deriveRow adds are positions: positional)
function prepare(accessor, base, theirs, mine) {
  const stored = [base, theirs, mine].map((row) => row ?? {});
  const r = stored.map((row) => deriveRow(accessor, row));
  return { n: r.map(normRow), r, stored };
}

// --- refs.require ------------------------------------------------------------
// Derived from the content (doBlockRefs, doEndpointRefs), never shown. Merged:
// the side that changed it; when both did, the union of theirs and mine (a
// require too many is harmless until the next save recomputes it; one
// missing would hide a use of a row).
function mergedRequire(base, theirs, mine) {
  const [rb, rt, rm] = [base, theirs, mine].map((row) => row?.refs?.require);
  if (isEqual(norm(rb), norm(rm))) return rt;
  if (isEqual(norm(rb), norm(rt))) return rm;
  if (!Array.isArray(rt) || !Array.isArray(rm)) return rt ?? rm;

  const out = rt.map((ref) => ({ ...ref, items: [...(ref?.items ?? [])] }));
  for (const ref of rm) {
    const into = out.find((other) => other?.type === ref?.type);
    if (!into) out.push(cloneDeep(ref));
    else
      for (const item of ref?.items ?? [])
        if (!into.items.some((other) => isEqual(other, item)))
          into.items.push(item);
  }
  return out;
}

// --- describe ----------------------------------------------------------------

// A value as a line shows it: a string, cut to MAX_VALUE characters
// (undefined: no value)
export function shown(value) {
  if (value === undefined) return undefined;
  const text = typeof value === "string" ? value : JSON.stringify(value);
  if (text === undefined) return undefined;
  return text.length > MAX_VALUE ? `${text.slice(0, MAX_VALUE - 1)}…` : text;
}

const elementShown = (element) =>
  element === undefined
    ? undefined
    : shown(element?.title ?? element?.field ?? element);

const arrayShown = (keyOf, base, side) => {
  if (!Array.isArray(side)) return shown(side);
  const kb = keyOf && keysOf(base, keyOf);
  const ks = keyOf && keysOf(side, keyOf);
  if (!kb || !ks) return `${side.length} elements`;
  const added = ks.filter((key) => !kb.includes(key)).length;
  const removed = kb.filter((key) => !ks.includes(key)).length;
  const common = ks.filter((key) => kb.includes(key));
  const parts = [
    added ? `${added} added` : null,
    removed ? `${removed} removed` : null,
    !isEqual(
      common,
      kb.filter((key) => ks.includes(key))
    )
      ? "order changed"
      : null
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "changed";
};

function lineOf(accessor, record, titleOf) {
  const { segs, side, kind, r, count } = record;
  const [rb, rt, rm] = r;
  const line = {
    path: pathOf(segs),
    label: labelOf(accessor, segs, { titleOf }),
    side,
    kind
  };

  if (kind === "count") {
    const text = [
      `${count.changed + count.added + count.removed} changed`,
      count.added ? `${count.added} added` : null,
      count.removed ? `${count.removed} removed` : null
    ]
      .filter(Boolean)
      .join(", ");
    return {
      ...line,
      base: undefined,
      theirs: side === "mine" ? undefined : text,
      mine: side === "theirs" ? undefined : text
    };
  }
  if (kind === "tree")
    return {
      ...line,
      base: undefined,
      theirs: side === "mine" ? undefined : treeSummary(rb, rt),
      mine: side === "theirs" ? undefined : treeSummary(rb, rm)
    };
  if (kind === "array") {
    const keyOf = KEYS[accessor]?.[segs[0]];
    return {
      ...line,
      base: Array.isArray(rb) ? `${rb.length} elements` : shown(rb),
      theirs: arrayShown(keyOf, rb, rt),
      mine: arrayShown(keyOf, rb, rm)
    };
  }
  if (kind === "added" || kind === "removed")
    return {
      ...line,
      base: elementShown(rb),
      theirs: elementShown(rt),
      mine: elementShown(rm)
    };
  return { ...line, base: shown(rb), theirs: shown(rt), mine: shown(rm) };
}

// The lines of what changed: side "theirs" (their save), "mine" (my changes),
// "both" (the two clash: a rebase is refused). Sorted both, theirs, mine; at
// most `limit`, `more` the lines left out. A row deleted on one side is one
// line (path "", kind "deleted").
export function describe({
  base,
  theirs,
  mine,
  accessor,
  limit = LIMIT,
  titleOf
}) {
  let records;

  if (theirs === null || mine === null) {
    const { n } = prepare(accessor, base, theirs ?? base, mine ?? base);
    const other = theirs === null ? n[2] : n[1];
    const otherChanged = !isEqual(n[0], other);
    const side =
      theirs === null && mine === null
        ? "theirs"
        : otherChanged
          ? "both"
          : theirs === null
            ? "theirs"
            : "mine";
    records = [
      {
        segs: [],
        side,
        kind: "deleted",
        r: [base ?? undefined, theirs ?? undefined, mine ?? undefined]
      }
    ];
  } else {
    const { n, r, stored } = prepare(accessor, base, theirs, mine);
    records = walk({ accessor, stored }, [], n, r).changes;
  }

  const lines = records
    .map((record, order) => ({ record, order }))
    .sort(
      (x, y) => RANK[x.record.side] - RANK[y.record.side] || x.order - y.order
    )
    .map(({ record }) =>
      record.kind === "deleted"
        ? {
            path: "",
            label: labelOf(accessor, []),
            side: record.side,
            kind: "deleted",
            base: base ? "stored" : undefined,
            theirs: theirs === null ? "deleted" : "changed",
            mine: mine === null ? "deleted" : "changed"
          }
        : lineOf(accessor, record, titleOf)
    );

  return {
    lines: lines.slice(0, Math.max(0, limit)),
    more: Math.max(0, lines.length - Math.max(0, limit))
  };
}

// Their changes only (theirs against base): the summary of a banner. Depends
// on base and theirs alone: memoise it per row and server version.
export const describeTheirs = ({ base, theirs, accessor, limit, titleOf }) =>
  describe({ base, theirs, mine: base, accessor, limit, titleOf });

// --- merge -------------------------------------------------------------------

// My changes applied on their version: { ok: true, row } when no field was
// changed by both (`both`: the paths that were) and neither side deleted the
// row, nor was it created in my draft (no base). The row is theirs (version,
// lastSaved, refs.belongs, refsComputed included) with my changes; the
// fields I did not change are theirs as stored (not derived). reason:
// "deleted" (theirs gone), "removed" (I deleted it), "created" (no base),
// "both".
export function merge({ base, theirs, mine, accessor }) {
  if (theirs === null || theirs === undefined)
    return { ok: false, row: null, both: [], reason: "deleted" };
  if (mine === null || mine === undefined)
    return { ok: false, row: null, both: [], reason: "removed" };
  if (base === null || base === undefined)
    return { ok: false, row: null, both: [], reason: "created" };

  const { n, r, stored } = prepare(accessor, base, theirs, mine);
  const result = walk({ accessor, stored }, [], n, r);
  if (result.both.length)
    return { ok: false, row: null, both: result.both, reason: "both" };

  const row = { ...result.value };
  // A field I did not change is theirs as stored (deriveRow adds the
  // sub-block ids and prevReference only to compare)
  const derived = r[1];
  for (const key of Object.keys(row)) {
    if (row[key] !== derived[key] && !isEqual(row[key], derived[key])) continue;
    if (theirs[key] === undefined) delete row[key];
    else row[key] = theirs[key];
  }

  // Never compared: theirs (version, lastSaved, refsComputed, refs.belongs)
  for (const key of NOT_COMPARED) {
    if (theirs[key] === undefined) delete row[key];
    else row[key] = theirs[key];
  }
  const require = mergedRequire(base, theirs, mine);
  if (require !== undefined) row.refs = { ...(theirs.refs ?? {}), require };

  return { ok: true, row: cloneDeep(row), both: [], reason: null };
}
